import {useMemo} from 'react'

import {parseFilterKeywords} from './utils/parse-filter-keywords'
import {filterConversationItems, filterThreads, type ParsedCommentsFilterState} from './utils/filter-items'
import {buildConversationItems, type ConversationItem} from './utils/build-conversation-items'
import {useDiffPathOrderMap, useIssueComments, useReviewSummaries, useThreads} from '../../stores/layout-selectors'
import {sortConversationItemsByDate, sortThreadItemsByDiffOrder} from './utils/sort-items'
import {getConversationItemsAuthors, type AuthorFilterItem} from './utils/get-author-filters'
import {useIssueCommentLiveUpdates} from '../../hooks/use-issue-comment-live-updates'
import {CommentsSort, type CommentsFilterState} from './use-comments-filter-state'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export interface CommentsFilterOverrides {
  selectedAuthor?: string
  hideResolved?: boolean
  filterText?: string
}

export interface UseFilteredCommentsDataOptions {
  /** Current filter state. */
  filterState: CommentsFilterState
  /** Optional overrides to apply to filters. */
  filterOverrides?: CommentsFilterOverrides
}

export interface FilteredCommentsData {
  /** Unique authors of unfiltered items, for the author filter. */
  authorList: AuthorFilterItem[]
  /** The review ID currently being filtered on, if any. */
  selectedReviewId: number | null

  /** Conversation items matching the conversation filter and sort direction. */
  filteredConversationItems: ConversationItem[]
  /** Total number of conversation items in this PR. */
  totalConversationItemsCount: number
}

const emptyObj = {} as const

const emptyItems = {
  unfilteredItems: [],
  filteredSortedItems: [],
}

export function useFilteredCommentsData({
  filterState,
  filterOverrides = emptyObj,
}: UseFilteredCommentsDataOptions): FilteredCommentsData {
  const filterStateWithOverrides = useMemo<ParsedCommentsFilterState>(
    () => ({
      ...filterState,
      filterText: undefined,
      selectedAuthor: filterOverrides?.selectedAuthor ?? filterState.selectedAuthor,
      hideResolvedThreads: filterOverrides?.hideResolved ?? filterState.hideResolvedThreads,
      parsedKeywords: parseFilterKeywords(filterOverrides?.filterText ?? filterState.filterText),
    }),
    [filterState, filterOverrides],
  )

  const threadPreviews = useThreads()

  const diffPathOrderMap = useDiffPathOrderMap()

  const issueComments = useIssueComments()
  useIssueCommentLiveUpdates()

  const reviewSummaries = useReviewSummaries()

  // Diff-order sort displays threads as flat list without issue comments or review summaries
  const diffOrderItems = useMemo(() => {
    if (filterStateWithOverrides.sort !== CommentsSort.DiffOrder) return emptyItems

    const unfilteredItems = buildConversationItems([], [], threadPreviews)
    const filteredSortedItems = sortThreadItemsByDiffOrder(
      filterConversationItems(unfilteredItems, filterStateWithOverrides),
      diffPathOrderMap,
    )

    return {unfilteredItems, filteredSortedItems}
  }, [filterStateWithOverrides, threadPreviews, diffPathOrderMap])

  // Date-order sort displays full list of comments and reviews with threads
  const dateOrderItems = useMemo(() => {
    if (filterStateWithOverrides.sort === CommentsSort.DiffOrder) return emptyItems

    // Items are built using filtered threads so that we don't show non-matching threads inside matching review items
    const threadsForItems = isFeatureEnabled('prs_docked_comments_replies')
      ? filterThreads(threadPreviews, filterStateWithOverrides)
      : []

    const unfilteredItems = buildConversationItems(issueComments, reviewSummaries, threadsForItems)
    const filteredSortedItems = sortConversationItemsByDate(
      filterConversationItems(unfilteredItems, filterStateWithOverrides),
      filterStateWithOverrides.sort,
    )

    return {unfilteredItems, filteredSortedItems}
  }, [filterStateWithOverrides, issueComments, reviewSummaries, threadPreviews])

  // Even though we resolve to a single value, the two separate memos help minimize dependencies per-mode to reduce
  // unnecessary calculations and renders, at a small complexity cost
  const resolvedItems = filterStateWithOverrides.sort === CommentsSort.DiffOrder ? diffOrderItems : dateOrderItems

  const authorList = useMemo(
    () => getConversationItemsAuthors(resolvedItems.unfilteredItems),
    [resolvedItems.unfilteredItems],
  )

  return {
    filteredConversationItems: resolvedItems.filteredSortedItems,
    selectedReviewId: filterStateWithOverrides.parsedKeywords?.reviewId ?? null,
    authorList,
    totalConversationItemsCount: resolvedItems.unfilteredItems?.length ?? 0,
  }
}
